import { focusAtom } from 'jotai-optics';
import { atomWithStorage } from 'jotai/utils';
import { BookingFlowPaths } from '../constants';
import { Snakelize } from '../types';
import { WalkerProfileRatesService, WalkerProfileServiceDetailed } from '../types/api';
import { subtractFromCurrentDate } from '../containers/AddPetInfoContainer/data';

import { SittingPreferences } from '../containers/OvernightServicePreferencesContainer/data';
import { GetWalkingPreferencesResponse } from '../containers/OvernightServiceContainer/api/getOvernightQuestionnaire';
import { PaymentFormValuesType } from '../containers/AddPaymentInfoContainer';
import { PostRegisterOwnerPayload } from '../hooks/api/usePostRegisterOwner';

export type PetInfo = {
  birthday?: string;
  breed?: string;
  isMixedBreed?: boolean;
  ageLabel?: string;
  ageValue?: number;
  gender?: string;
  sterile?: string;
  size?: string;
  name?: string;
  imageDataUrl?: string;
};

export type Schedule = Snakelize<{
  timeLabels: {
    start: string;
    end: string;
  };
  startDate: string;
  endDate: string;
  startIsoDate: string;
  endIsoDate: string;
  startTime: string;
  endTime: string;
  isAsap: boolean;
  enableScheduleBackup: boolean;
  timeWindows: Array<{
    id: string;
    // eslint-disable-next-line camelcase
    is_ideal: boolean;
  }>;
}>;

export type Notes = Snakelize<{
  sittingQuestionnaireSlug?: string;
  sittingNotes: string;
  sittingQuestionnaireAnswers: Array<{
    value: string;
    identifier: string;
  }>;
  ownerMessage: string;
}>;

export type WalkerProfileRates = {
  description?: string;
  services: WalkerProfileRatesService[];
  servicesDetailed?: WalkerProfileServiceDetailed[];
  name?: string;
  promoCode?: string;
  pictureUrl?: string;
  servicesLabels?: string[];
  uuid?: string;
};

export type BookingFlowAtom = {
  stageData: {
    currentStage: string;
  };
  petInfo: PetInfo;
  anonUserId?: string;
  paymentFormValues?: PaymentFormValuesType;
  attribution: PostRegisterOwnerPayload['attribution'];
  stripeToken?: string;
  ownerAuth?: {
    id?: string;
    // This is the JWT authentication token, rename this to jwtAuthToken
    sessionToken?: string;
    refreshToken?: string;
  };
  session?: {
    /*
      This is the session token to make requests
      to the authentication endpoints such as "/code" or /verify"
    */
    sessionToken?: string;
  };
  selectedServiceCategory: string;
  selectedServiceType: string;
  walkPreferences: GetWalkingPreferencesResponse;
  ownerInfo: {
    email: string;
  };
  notes: Notes;
  schedule: Schedule;
  selectedWalkerInfo: {
    rates?: WalkerProfileRates;
    name?: string;
    promoCode?: string;
    pictureUrl?: string;
    servicesLabels?: string[];
    uuid?: string;
  };
  estimatedPriceId?: number;
  paymentContainerErrorText?: string;
};

export const bookingFlowInitialData: BookingFlowAtom = {
  selectedServiceType: '',
  estimatedPriceId: null,
  session: {
    sessionToken: '',
  },
  attribution: null,
  selectedServiceCategory: 'Walk',
  paymentFormValues: null,
  ownerInfo: {
    email: '',
  },
  stageData: {
    currentStage: BookingFlowPaths.AddPetInfo,
  },
  anonUserId: '',
  walkPreferences: SittingPreferences,
  petInfo: {
    birthday: subtractFromCurrentDate(0, 7),
    breed: '',
    isMixedBreed: false,
    ageLabel: '2 years',
    ageValue: 5,
    gender: '',
    size: '',
    sterile: '',
    name: '',
    imageDataUrl: '',
  },
  schedule: {
    time_labels: {
      start: '',
      end: '',
    },
    is_asap: false,
    time_windows: [],
    start_date: '',
    start_iso_date: '',
    end_date: '',
    end_iso_date: '',
    start_time: '',
    end_time: '',
    enable_schedule_backup: true,
  },
  notes: {
    sitting_questionnaire_slug: '',
    sitting_notes: '',
    sitting_questionnaire_answers: [],
    owner_message: '',
  },
  selectedWalkerInfo: {
    rates: {
      description: '',
      services: [],
      servicesDetailed: [],
    },
    name: '',
    promoCode: '',
    pictureUrl: '',
    servicesLabels: [],
    uuid: '',
  },
  paymentContainerErrorText: '',
};

export const bookingFlowAtom = atomWithStorage('state', bookingFlowInitialData);
export const petInfoAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('petInfo'));
export const petNameAtom = focusAtom(petInfoAtom, (optic) => optic.prop('name'));
export const ownerInfoAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('ownerInfo'));
export const ownerEmailAtom = focusAtom(ownerInfoAtom, (optic) => optic.prop('email'));
export const stageAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('stageData'));
export const currentStageAtom = focusAtom(stageAtom, (optic) => optic.prop('currentStage'));
export const selectedServiceCategoryAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('selectedServiceCategory'));
export const selectedServiceTypeAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('selectedServiceType'));
export const selectedWalkerInfoAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('selectedWalkerInfo'));
export const walkerNameAtom = focusAtom(selectedWalkerInfoAtom, (optic) => optic.prop('name'));
export const ratesAtom = focusAtom(selectedWalkerInfoAtom, (optic) => optic.prop('rates'));
export const servicesAtom = focusAtom(ratesAtom, (optic) => optic.prop('services'));
export const servicesDetailedAtom = focusAtom(ratesAtom, (optic) => optic.prop('servicesDetailed'));
export const scheduleAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('schedule'));
export const notesAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('notes'));
export const walkPreferencesServiceInfoAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('walkPreferences'));
export const anonUserIdAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('anonUserId'));
export const ownerAuthAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('ownerAuth'));
export const stripeTokenAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('stripeToken'));
export const estimatedPriceIdAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('estimatedPriceId'));
export const paymentFormValuesAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('paymentFormValues'));
export const attributionAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('attribution'));
export const paymentContainerErrorTextAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('paymentContainerErrorText'));
export const sessionAtom = focusAtom(bookingFlowAtom, (optic) => optic.prop('session'));
export const sessionTokenAtom = focusAtom(sessionAtom, (optic) => optic.prop('sessionToken'));
